// admin/src/screens/PlansPacks.jsx — plans (real Stripe price ids) + top-up packs. Both are
// upserts by id, so a blank "new" row creates one. The placeholder-over-real refusal is
// server-side (planPut) — this surfaces it honestly (a refusal classifies as kind:'refused',
// NOT ok; the skeleton's `ok && refused` branch was dead — handled correctly here).

import React, { useEffect, useState } from 'react';
import { useAdmin } from '../App.jsx';
import { Banner } from '../lib/ui.jsx';

const isRealPrice = (v) => !!(v && !/^price_PLACEHOLDER/.test(v));

function EditableRow({ row, fields, isNew, onSave }) {
  const [v, setV] = useState(row);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setMsg(null); setErr(null); setBusy(true);
    const r = await onSave(v);
    setBusy(false);
    if (r.kind === 'ok' && r.body.ok) { setMsg(r.body.created ? 'created' : 'saved'); if (isNew) setV(row); }
    else if (r.kind === 'refused' || r.kind === 'invalid' || r.kind === 'auth') setErr(r.error);
    else setErr(r.error || 'failed');
  }

  return (
    <tr>
      {fields.map((f) => (
        <td key={f.key}>
          {f.readOnly && !isNew ? <span className="mono muted">{String(v[f.key] ?? '—')}</span>
            : f.options ? (
              <select value={v[f.key] ?? ''} onChange={(e) => setV({ ...v, [f.key]: e.target.value })}>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input className={f.mono ? 'mono' : ''} style={{ inlineSize: f.wide ? 240 : 100 }}
                placeholder={f.placeholder || ''} value={v[f.key] ?? ''}
                onChange={(e) => setV({ ...v, [f.key]: f.number ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value })} />
            )}
          {f.key === 'stripe_price_id' && !isNew && (
            isRealPrice(v[f.key])
              ? <span className="pill active" style={{ marginInlineStart: 6 }}>real</span>
              : v[f.key] ? <span className="pill warn" style={{ marginInlineStart: 6 }}>placeholder</span>
                : <span className="pill retired" style={{ marginInlineStart: 6 }}>none</span>
          )}
        </td>
      ))}
      <td>
        <button className="btn tiny" disabled={busy} onClick={save}>{isNew ? 'Create' : 'Save'}</button>
        {msg && <span className="okmsg"> {msg}</span>}
        {err && <div className="err" style={{ maxInlineSize: 320 }}>{err}</div>}
      </td>
    </tr>
  );
}

export default function PlansPacks() {
  const { api } = useAdmin();
  const [plans, setPlans] = useState(null);
  const [packs, setPacks] = useState(null);
  const [err, setErr] = useState(null);

  async function load() {
    const p = await api.get('admin/plans');
    if (p.kind === 'ok') setPlans(p.body.plans); else setErr(p.error);
    const k = await api.get('admin/packs');
    if (k.kind === 'ok') setPacks(k.body.packs); else setErr(k.error); // don't leave packs a permanent "Loading…"
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const planFields = [
    { key: 'id', readOnly: true, mono: true, placeholder: 'plan_id' },
    { key: 'label', wide: true },
    { key: 'monthly_grant_credits', number: true },
    { key: 'grant_expires_days', number: true, placeholder: 'null' },
    { key: 'stripe_price_id', wide: true, mono: true, placeholder: 'price_…' },
    { key: 'status', options: ['active', 'retired'] },
  ];
  const savePlan = (id) => (v) => api.put(`admin/plans/${id}`, {
    label: v.label, monthly_grant_credits: Number(v.monthly_grant_credits) || 0,
    grant_expires_days: v.grant_expires_days === '' || v.grant_expires_days == null ? null : Number(v.grant_expires_days),
    stripe_price_id: v.stripe_price_id || null, status: v.status || 'active',
  });

  const packFields = [
    { key: 'id', readOnly: true, mono: true, placeholder: 'pack_id' },
    { key: 'label', wide: true },
    { key: 'credits', number: true },
    { key: 'usd_cents', number: true },
    { key: 'sort', number: true },
    { key: 'status', options: ['active', 'retired'] },
  ];
  const savePack = (id) => (v) => api.put(`admin/packs/${id}`, {
    label: v.label, credits: Number(v.credits), usd_cents: Number(v.usd_cents),
    sort: Number(v.sort) || 0, status: v.status || 'active',
  });

  return (
    <div>
      <h1>Plans &amp; packs</h1>
      {err && <Banner kind="err">{err}</Banner>}
      <Banner kind="info">
        Prices here are LIVE data — the seeded numbers are placeholders (flagged since C2); set the real Stripe price ids before the switch-on.
        A real price id can never be clobbered by a placeholder (the server refuses that one direction).
      </Banner>

      <h2>Plans</h2>
      {!plans ? <p className="muted">Loading…</p> : (
        <table>
          <thead><tr><th>id</th><th>label</th><th className="num">monthly grant</th><th className="num">expires (days)</th><th>stripe price id</th><th>status</th><th /></tr></thead>
          <tbody>
            {plans.map((p) => <EditableRow key={p.id} row={p} fields={planFields} onSave={savePlan(p.id)} />)}
            <NewRow fields={planFields} label="plan" onSave={(v) => v.id ? savePlan(v.id)(v) : Promise.resolve({ kind: 'invalid', error: 'an id is required' })} />
          </tbody>
        </table>
      )}

      <h2>Top-up packs</h2>
      {!packs ? <p className="muted">Loading…</p> : (
        <table>
          <thead><tr><th>id</th><th>label</th><th className="num">credits</th><th className="num">USD cents</th><th className="num">sort</th><th>status</th><th /></tr></thead>
          <tbody>
            {packs.map((p) => <EditableRow key={p.id} row={p} fields={packFields} onSave={savePack(p.id)} />)}
            <NewRow fields={packFields} label="pack" onSave={(v) => v.id ? savePack(v.id)(v) : Promise.resolve({ kind: 'invalid', error: 'an id is required' })} />
          </tbody>
        </table>
      )}
    </div>
  );
}

// a blank create row — the id is editable (both plans and packs are upserts by id)
function NewRow({ fields, label, onSave }) {
  const editableFields = fields.map((f) => (f.key === 'id' ? { ...f, readOnly: false } : f));
  const blank = Object.fromEntries(fields.map((f) => [f.key, f.options ? f.options[0] : '']));
  return (
    <>
      <tr><td colSpan={fields.length + 1} className="hint" style={{ paddingBlockStart: 12 }}>+ new {label} (upsert by id)</td></tr>
      <EditableRow row={blank} fields={editableFields} isNew onSave={onSave} />
    </>
  );
}
